<template>
  <Modal
    as-form
    open
    persistent
    :size="ModalSize.XL"
    :title="modalTitle"
    @submit="handleSocialPost"
  >
    <template #body>
      <Alert
        v-if="!props.socialData.isEditable && !props.socialData.isCustomizable"
        class="mt-8"
      >
        {{ $t('personalize-post-modal.locked-warning') }}
      </Alert>

      <Alert v-if="postingAvailability" class="mt-8">
        {{ postingAvailability }}
      </Alert>

      <PostModalContent
        ref="postModalContent"
        class="flex-1 mb-8"
        :social-data="formData"
        :is-editable="props.socialData.isEditable"
        :is-customizable="props.socialData.isCustomizable"
        :availability-start-date="parsedAvailabilityStartDate"
        :visibility-end-date="props.content?.endDate ?? undefined"
        :quadrants="props.socialData.quadrants"
        :original-dimensions="props.socialData.originalDimensions"
        disable-social-select
        @update:social-data="formData = $event"
      />
    </template>
    <template #actions="{ closeModal }">
      <div class="py-5 px-4 sm:px-12 border-t flex justify-between">
        <Btn :size="BtnSize.LG" @click="handleClose(closeModal)">
          {{ $t('common.cancel') }}
        </Btn>
        <div class="flex gap-2">
          <Btn
            v-if="props.socialData.isCustomizable"
            :color="BtnColor.PRIMARY"
            :variant="BtnVariant.OUTLINED"
            :size="BtnSize.LG"
            @click="handleSaveSocialPostAsDraft()"
          >
            {{ $t('personalize-post-modal.save-as-draft') }}
          </Btn>
          <Btn
            :color="BtnColor.PRIMARY"
            :variant="BtnVariant.FILLED"
            :size="BtnSize.LG"
            @click="validateVariablesBeforeSubmit"
          >
            {{
              formData.isScheduled
                ? $t('personalize-post-modal.schedule')
                : $t('personalize-post-modal.post-now')
            }}
          </Btn>
        </div>
      </div>
    </template>
  </Modal>

  <SaveAsDraftModal
    v-if="showSaveAsDraftModal"
    @cancel="showSaveAsDraftModal = false"
    @close="handleCloseSaveAsDraftModal"
    @submit="handleSaveSocialPostAsDraft"
  />
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { Btn, BtnColor, BtnSize, BtnVariant } from '~/components/btn';
import {
  createStorageFilePath,
  ref,
  useFirebase,
  useSocialRedirectUrl,
} from '#imports';
import {
  apiEditSocialPost,
  apiPostAgainSocialPost,
  SocialPostDto,
  UpdateSocialPostBodyDto,
} from '~/api/socials';
import { useLoader } from '~/composables/use-loader';
import { PostModalContent } from '~/pages/my-posts/_components/post-modal-content';
import { format, isFuture, parseISO } from 'date-fns';
import { Alert } from '~/components/alert';
import {
  apiCreateContentSocialDraft,
  apiCreateSocialShareUrl,
  apiCreateSocialShareUrlFromDraft,
  apiUpdateContentSocialDraft,
  ContentDto,
  ContentSocialDraftDto,
  CreateContentSocialDraftBodyDto,
  CreateSocialShareUrlBodyDto,
} from '~/api/contents';
import { SocialPostStatus } from '~/pages/my-posts/my-posts.constants';
import {
  getDownloadURL,
  ref as getStorageRef,
  uploadString,
} from 'firebase/storage';
import { create as createContentDisposition } from 'content-disposition-header';
import { useCustomDescription } from '~/composables/use-custom-description';
import { useVariablesModalStore } from '~/store/variables-modal';
import { ContentSocialDto, HubDto } from '~/api/hubs';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import differenceBy from 'lodash/differenceBy';
import isEmpty from 'lodash/isEmpty';
import { SaveAsDraftModal } from '~/pages/my-posts/_components/edit-post-modal/_components/save-as-draft-modal';
import { Modal, ModalSize } from '~/components/modals/modal';
import { AccountType } from '~/api/accounts';
import { createContentLaunchMetric, LaunchMetricType } from '~/api/metrics';
import { storeToRefs } from 'pinia';
import { useAccountStore } from '~/store/account';
import { apiCreateContentFile } from '~/api/files';
import { PostModalContentForm } from '~/pages/my-posts/_components/post-modal-content/post-modal-content.types';

type Props = {
  socialData: SocialPostDto & ContentSocialDto;
  hub?: HubDto;
  content?: ContentDto;
  contentData?: { hubSlug: string; contentSlug: string; schedule: boolean };
  socialDraft?: ContentSocialDraftDto;
  isScheduled?: boolean;
};

const props = defineProps<Props>();

const emit = defineEmits<{
  close: [];
  submit: [];
  submitDraft: [];
}>();

const { t } = useI18n();

const { currentAccount } = storeToRefs(useAccountStore());

const { setSubmitCallback } = useVariablesModalStore();
const { validateVariables, getCustomDescription, activateVariablesModal } =
  useCustomDescription(props.socialData.description || '');

const modalTitle = computed(() =>
  props.contentData
    ? props.contentData.schedule
      ? t('edit-post-modal.title')
      : t('personalize-post-modal.title')
    : t('edit-post-modal.title'),
);

const postingAvailability = computed<string | null>(() => {
  const formatPattern = 'MMMM d, yyyy';
  const visibilityEndDate = props.content?.endDate ?? null;
  const availabilityStartDate = props.socialData.isAvailabilityDateEnabled
    ? props.socialData.availabilityStartDate
    : null;

  if (visibilityEndDate === null) {
    if (availabilityStartDate !== null && isFuture(availabilityStartDate)) {
      return t('content.post-availability.starting', {
        date: format(parseISO(availabilityStartDate), formatPattern),
      });
    }

    return null;
  }

  if (availabilityStartDate !== null && isFuture(availabilityStartDate)) {
    return t('content.post-availability.range', {
      start: format(parseISO(availabilityStartDate), formatPattern),
      end: format(parseISO(visibilityEndDate), formatPattern),
    });
  }

  return t('content.post-availability.until', {
    date: format(parseISO(visibilityEndDate), formatPattern),
  });
});

const parsedLogos = computed(() => {
  const parsed = props.socialDraft?.logos.map((logo) => {
    const parsedLogo = {
      ...omit(logo, ['accountLogo', 'id']),
      accountLogoId: logo.accountLogo.id,
    };

    return parsedLogo;
  });
  return parsed;
});

const parsedAvailabilityStartDate = computed<undefined | string>(() => {
  if (!props.socialData.isAvailabilityDateEnabled) {
    return;
  }
  return props.socialData.availabilityStartDate ?? undefined;
});

const logoFromDraft = parsedLogos.value ? parsedLogos.value[0] : [];
const logosFromDraft = parsedLogos.value || [];

const formData = ref<PostModalContentForm>({
  socialFiles: props.socialData.socialFiles,
  type: props.socialData.type,
  description: props.socialData.description as string,
  postPlannedAt: props.socialData.postPlannedAt || null,
  isScheduled:
    Boolean(props.socialData.postPlannedAt) ||
    Boolean(props.contentData?.schedule) ||
    props.isScheduled,
  date: props.socialData.postPlannedAt
    ? format(new Date(props.socialData.postPlannedAt as string), 'MM/dd/yy')
    : '',
  time: props.socialData.postPlannedAt
    ? format(new Date(props.socialData.postPlannedAt as string), 'hh:mm aa')
    : '',
  fromContent:
    Boolean(props.contentData) || Boolean(props.socialData.contentTitle),
  descriptionErrorState: {},
  logo: logoFromDraft,
  logos: logosFromDraft,
  imageData: null,
});

const wasSubmited = ref<boolean>(false);
const postModalContent = ref<any>(null);

const showSaveAsDraftModal = ref<boolean>(false);
const isDraftDataChanged = computed(
  () =>
    !isEmpty(formData.value.logo) ||
    props.socialData.description !== formData.value.description,
);

const parseAccountLogoId = (accountLogoId: number | string): number => {
  if (typeof accountLogoId === 'number') {
    return accountLogoId;
  }

  let parsedId: number;

  if (accountLogoId.includes('-quadrant-')) {
    const [idPart] = accountLogoId.split('-quadrant-');
    parsedId = Number.parseInt(idPart, 10);
  } else {
    parsedId = Number.parseInt(accountLogoId, 10);
  }

  if (Number.isNaN(parsedId)) {
    throw new TypeError(`Invalid account logo ID format: ${accountLogoId}`);
  }

  return parsedId;
};

const handleSaveSocialPostAsDraft = async (isPublishing?: boolean) => {
  let allLogos: any[] = [];
  if (formData.value.logos && formData.value.logos.length > 0) {
    allLogos = formData.value.logos;
  } else if (postModalContent.value?.getAllLogos) {
    try {
      const accountLogoId = formData.value.logo?.accountLogoId;
      if (accountLogoId) {
        const parsedAccountLogoId = parseAccountLogoId(accountLogoId);
        allLogos = postModalContent.value.getAllLogos(parsedAccountLogoId);
      } else {
        allLogos = postModalContent.value.getAllLogos() || [];
      }
    } catch {
      allLogos = [];
    }
  } else if (formData.value.logo?.accountLogoId && props.socialDraft) {
    allLogos = [formData.value.logo];
  }

  const socialBody: CreateContentSocialDraftBodyDto = {
    contentSocialId: props.socialData.id,
    finishedAt: null,
    description: formData.value.description,
    logos: allLogos,
  };

  if (isPublishing) {
    socialBody.finishedAt = new Date().toISOString();
  }

  const draft = await apiCreateContentSocialDraft(socialBody);

  if (allLogos.length > 0) {
    formData.value.logos = allLogos;
  }

  emit('submitDraft');

  if (isPublishing) {
    return draft;
  }

  emit('close');
  return draft;
};

const handleDraft = async (): Promise<number | undefined> => {
  if (!props.socialDraft?.finishedAt && props.socialDraft) {
    await apiUpdateContentSocialDraft(props.socialDraft.id, {
      finishedAt: new Date().toISOString(),
    });
    return props.socialDraft.id;
  }

  if (props.socialData.isCustomizable && !props.socialDraft) {
    const draft = await handleSaveSocialPostAsDraft(true);

    if (!draft?.id) {
      return undefined;
    }

    return draft.id;
  }

  return undefined;
};

const { firebaseStorage } = useFirebase();

const saveRenderedImage = async (dataUrl: string) => {
  const fileName = `${crypto.randomUUID()}.jpeg`;

  const storageRef = getStorageRef(
    firebaseStorage,
    createStorageFilePath(fileName),
  );

  const metadata = {
    contentType: 'image/jpeg',
    contentDisposition: createContentDisposition(fileName),
  };

  const snapshot = await uploadString(
    storageRef,
    dataUrl.slice(23),
    'base64',
    metadata,
  );

  return await getDownloadURL(snapshot.ref);
};

const getSocialShareUrl = async (overrideDraftId?: number) => {
  let socialFiles: any[] = [];

  const areCaptionsUpdated = !isEmpty(
    differenceBy(
      props.socialData.socialFiles,
      formData.value.socialFiles,
      'caption',
    ),
  );

  if (areCaptionsUpdated) {
    socialFiles = formData.value.socialFiles.map((socialFile) => ({
      fileId: socialFile.file.id,
      caption: socialFile.caption,
    }));
  }

  const draftId = overrideDraftId || props.socialDraft?.id;

  const socialShareUrlBody: CreateSocialShareUrlBodyDto = {
    contentSocialId: props.socialData.id,
    description: formData.value.description,
    ...(draftId && { draftId }),
    ...(formData.value.postPlannedAt && {
      postPlannedAt: formData.value.postPlannedAt,
    }),
    ...(socialFiles.length > 0 && {
      socialFiles,
    }),
  };

  if (props.socialDraft || overrideDraftId || !props.contentData?.hubSlug) {
    const { shareUrl } =
      await apiCreateSocialShareUrlFromDraft(socialShareUrlBody);

    return shareUrl;
  }

  if (props.contentData) {
    const { shareUrl } = await apiCreateSocialShareUrl(
      props.contentData.hubSlug,
      props.contentData.contentSlug,
      socialShareUrlBody,
    );

    if (
      props.hub?.isLaunchEnabled &&
      currentAccount.value?.type === AccountType.STANDARD &&
      props.content &&
      !props.content.launchData.completedAt
    ) {
      await createContentLaunchMetric(
        props.hub.slug,
        props.content.slug,
        LaunchMetricType.COMPLETED,
      );
      // eslint-disable-next-line vue/no-mutating-props
      props.content.launchData.completedAt = new Date();
    }

    return shareUrl;
  }
};

const isDescriptionValid = () => {
  const shouldValidateDescription =
    formData.value.descriptionErrorState && !props.socialData.isEditable;

  if (!shouldValidateDescription) {
    return true;
  }

  return validateVariables(formData.value.descriptionErrorState);
};

const setSocialPostDescription = (
  submitCallback: () => Promise<void> | void,
) => {
  setSubmitCallback(submitCallback);
  activateVariablesModal(formData.value.descriptionErrorState);
};

const setSocialPostPlanDate = () => {
  if (formData.value.date && formData.value.time) {
    const fullDate = new Date(`${formData.value.date} ${formData.value.time}`);
    formData.value.postPlannedAt = fullDate.toISOString();
  } else {
    formData.value.postPlannedAt = null;
  }
};

const schedulePost = async (
  socialBody: UpdateSocialPostBodyDto,
): Promise<void> => {
  if (!props.socialData.isEditable) {
    formData.value.description = getCustomDescription();
  }

  await apiEditSocialPost(props.socialData.id, {
    ...socialBody,
    type: undefined,
    socialFiles: formData.value.socialFiles.map((socialFile) => ({
      fileId: socialFile.file.id,
      caption: socialFile.caption,
    })),
  });
  emit('close');
  emit('submit');
};

const retryFailedSocialPost = async (
  socialBody: Record<string, any>,
): Promise<void> => {
  const { shareUrl } = await apiPostAgainSocialPost(props.socialData.id, {
    ...pick(socialBody, ['description', 'postPlannedAt']),
    ...(props.socialData.isEditable && {
      socialFiles: formData.value.socialFiles.map((socialFile) => ({
        fileId: socialFile.file.id,
        caption: socialFile.caption,
      })),
    }),
  });
  const { updateSocialRedirectUrl } = useSocialRedirectUrl();

  updateSocialRedirectUrl();
  globalThis.location.href = shareUrl;
};

const handleSocialShareRedirect = async () => {
  const description = props.socialData.isEditable
    ? formData.value.description
    : getCustomDescription();

  let finalDraftId = props.socialDraft?.id;

  if (props.socialData.isCustomizable && !props.socialDraft) {
    const newDraft = await handleSaveSocialPostAsDraft(true);
    finalDraftId = newDraft?.id;

    if (!finalDraftId) {
      throw new Error('Failed to create draft: Draft ID is undefined');
    }
  }

  const { shareUrl } = await apiCreateSocialShareUrlFromDraft({
    contentSocialId: props.socialData.id,
    draftId: finalDraftId,
    description,
    ...(formData.value.postPlannedAt && {
      postPlannedAt: formData.value.postPlannedAt,
    }),
  });

  if (props.socialDraft && !props.socialDraft.finishedAt) {
    await apiUpdateContentSocialDraft(props.socialDraft.id, {
      finishedAt: new Date().toISOString(),
    });
  }

  const { updateSocialRedirectUrl } = useSocialRedirectUrl();
  updateSocialRedirectUrl();

  if (shareUrl) {
    globalThis.location.href = shareUrl;
  }
};

const handleSocialPost = async () => {
  setSocialPostPlanDate();
  await useLoader({
    action: async () => {
      if (
        props.socialDraft ||
        (props.contentData && props.socialData.isCustomizable)
      ) {
        return await handleSocialShareRedirect();
      }

      if (props.contentData) {
        const shareUrl = await getSocialShareUrl();

        if (!shareUrl) {
          throw new Error('Failed to get share URL');
        }

        const { updateSocialRedirectUrl } = useSocialRedirectUrl();
        updateSocialRedirectUrl();

        globalThis.location.href = shareUrl;
        return;
      }

      const socialBody = {
        type: formData.value.type,
        postPlannedAt: formData.value.postPlannedAt,
        ...((!props.socialData.contentSocial ||
          props.socialData.contentSocial.isEditable) && {
          description: formData.value.description,
        }),
      };

      if (
        props.socialData.isCustomizable &&
        !props.socialDraft &&
        props.socialData.status !== SocialPostStatus.RESCHEDULE_REQUIRED
      ) {
        await handleSaveSocialPostAsDraft(true);
      }

      if (
        props.socialData.status === SocialPostStatus.SCHEDULED ||
        props.socialData.status === SocialPostStatus.RESCHEDULE_REQUIRED
      ) {
        return await schedulePost(socialBody);
      }

      if (props.socialData.status === SocialPostStatus.FAILED) {
        return await retryFailedSocialPost(socialBody);
      }
    },
  });
};

const handleClose = (closeModal: () => void) => {
  if (
    isDraftDataChanged.value &&
    !wasSubmited.value &&
    !props.socialDraft &&
    props.socialData.isCustomizable
  ) {
    showSaveAsDraftModal.value = true;
    return;
  }
  emit('close');
  closeModal();
};

const handleCloseSaveAsDraftModal = () => {
  showSaveAsDraftModal.value = false;
  emit('close');
};

const downloadDataUrl = (dataUrl: string, filename: string) => {
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
};

const validateVariablesBeforeSubmit = (e: MouseEvent) => {
  if (!(e.target instanceof HTMLButtonElement))
    throw new Error('This callback should be bound to a submit button');
  if (isDescriptionValid()) {
    wasSubmited.value = true;
    e.target.form?.dispatchEvent(new Event('submit'));
  } else {
    setSocialPostDescription(() => validateVariablesBeforeSubmit(e));
  }
};
</script>
